import React, { useState, useEffect } from 'react';
import { Building, Users, Lock, ArrowUp } from 'lucide-react';
import OptionCard from '../../components/OptionCard';
import StepContainer from '../../components/StepContainer';
import { useAuth } from '../../context/AuthContext';
import { useSubscription } from '../../context/subscription/SubscriptionContext';
import { PLAN_TIERS } from '../../types/subscription';

interface LandingScreenProps {
  onBuildAudience: () => void;
  onSelectExisting: () => void;
}

const LandingScreen: React.FC<LandingScreenProps> = ({
  onBuildAudience,
  onSelectExisting
}) => {
  const { isAuthenticated } = useAuth();
  const { currentSubscription } = useSubscription();
  const [audienceData, setAudienceData] = useState<{ count: number; total?: number; limit: number; can_create: boolean } | null>(null);
  const [hasSubmittedRequest, setHasSubmittedRequest] = useState<boolean>(false);

  // Check if user has any audiences based on the total from API
  const hasAudiences = audienceData !== null && (audienceData.total ?? 0) > 0;

  // Check for submitted request state on mount
  useEffect(() => {
    const submittedState = localStorage.getItem('audience_request_submitted');
    if (submittedState === 'true') {
      setHasSubmittedRequest(true);
    }
  }, []);

  // Fetch audience count and limit
  useEffect(() => {
    const fetchAudienceData = async () => {
      if (isAuthenticated) {
        try {
          const response = await fetch(`${import.meta.env.VITE_API_URL}/audience/count`, {
            credentials: 'include'
          });
          if (response.ok) {
            const data = await response.json();
            setAudienceData(data);
          }
        } catch (error) {
          console.error('Error fetching audience data:', error);
        }
      }
    };
    
    fetchAudienceData();
  }, [isAuthenticated]);

  // Check if user can create audiences
  const isEnterprise = currentSubscription?.id === PLAN_TIERS.ENTERPRISE;
  const isDisabled = audienceData?.can_create === false || (isEnterprise && hasSubmittedRequest);
  
  // Generate icon based on disabled state
  let icon = <Building className="w-10 h-10" />;
  if (isDisabled) {
    if (isEnterprise && hasSubmittedRequest) {
      icon = <ArrowUp className="w-10 h-10" />;
    } else {
      icon = <Lock className="w-10 h-10" />;
    }
  }
  return (
    <StepContainer
      title="What would you like to do today?"
      className="animate-fadeIn"
    >
      <div className="grid md:grid-cols-2 gap-6">
        <OptionCard
          icon={icon}
          title="Build a New Audience"
          description="Create a new set of customer profiles tailored to your specific needs. Build detailed profiles for simulation."
          buttonText={
            <div className="flex items-center justify-center">
              Build Audience
              {/* Show count for non-enterprise users */}
              {!isEnterprise && audienceData && audienceData.limit !== -1 && (
                <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${
                  audienceData.can_create
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                }`}>
                  {audienceData.count}/{audienceData.limit === 0 ? '0' : audienceData.limit}
                </span>
              )}
            </div>
          }
          onClick={onBuildAudience}
          disabled={isDisabled}
        />
        
        <OptionCard
          icon={hasAudiences ? <Users className="w-10 h-10" /> : <Lock className="w-10 h-10" />}
          title="Simulation Playground"
          description="Use an existing audience to simulate customer behavior, test ideas, and gain valuable insights."
          buttonText={hasAudiences ? "Select Existing Audience" : "No Audiences Available"}
          onClick={onSelectExisting}
          disabled={!hasAudiences}
        />
      </div>
    </StepContainer>
  );
};

export default LandingScreen;